import { Check, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { type StripePlan, STRIPE_PRODUCTS, getPlanBenefits } from '@/lib/stripe-config';
import { cn } from '@/lib/utils';
import { trackPricingCardInteraction } from '@/lib/analytics';

interface PricingCardProps {
  plan: StripePlan;
  isCurrentPlan: boolean;
  isRecommended?: boolean;
  isAnnual?: boolean;
  // VOY-601: the Stripe price for the selected interval, from subscription_prices. Null while the
  // prices load or when none is sellable: the button is disabled.
  priceId: string | null;
  onSubscribe: (priceId: string) => void;
  loading?: boolean;
}

// "Best for" descriptions following Notion/Slack narrative approach - Benefit-focused
// Updated for new tier names (Pro, Business, Family)
const PLAN_DESCRIPTIONS: Record<StripePlan, string> = {
  VOY_FREE: 'Browse at standard prices. Upgrade anytime to unlock special subscriber pricing.',
  VOY_PRO: 'Unlock special subscriber pricing on all bookings. Perfect for solo travelers.',
  VOY_BUSINESS: 'Special subscriber pricing. Ideal for frequent business travelers.',
  VOY_FAMILY: 'Special pricing for the whole family. Book for anyone with maximum flexibility.',
};

export function PricingCard({
  plan,
  isCurrentPlan,
  isRecommended = false,
  isAnnual = false,
  priceId,
  onSubscribe,
  loading = false,
}: PricingCardProps) {
  const product = STRIPE_PRODUCTS[plan];
  const benefits = getPlanBenefits(plan);
  const description = PLAN_DESCRIPTIONS[plan];
  
  // Calculate annual pricing (20% discount)
  const monthlyPrice = product.price;
  const annualPrice = monthlyPrice * 12 * 0.8; // 20% off
  const monthlyEquivalent = annualPrice / 12;

  return (
    <Card
      className={cn(
        'relative rounded-2xl border-2 p-6 md:p-8 transition-all duration-300',
        // Recommended plan: scale, border highlight, glassmorphism background
        isRecommended
          ? 'border-[#5B6FED] shadow-xl md:scale-105 bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl dark:border-blue-500/50'
          : 'border-white/30 dark:border-zinc-700/50 hover:border-white/40 dark:hover:border-zinc-600/50 hover:shadow-lg bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl',
        // Current plan: ring indicator
        isCurrentPlan && 'ring-2 ring-[#5B6FED] ring-offset-2'
      )}
    >
      {/* Recommended Badge */}
      {isRecommended && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
          <span className="bg-[#5B6FED] text-white px-4 py-1.5 rounded-full text-sm font-semibold shadow-lg">
            Most Popular
          </span>
        </div>
      )}

      {/* Current Plan Badge */}
      {isCurrentPlan && (
        <div className="absolute top-4 right-4 z-10">
          <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
            Current Plan
          </span>
        </div>
      )}

      {/* Plan Header */}
      <div className="mb-4 md:mb-6">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-xl md:text-2xl font-semibold text-white">{product.name}</h3>
          {product.tier !== 'free' && (
            <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-xs">
              <Sparkles className="h-3 w-3 mr-1" />
              Special Pricing
            </Badge>
          )}
        </div>
        <p className="text-sm md:text-base text-white/80 leading-relaxed">{description}</p>
      </div>

      {/* Price Display - Following Notion/Slack typography */}
      <div className="mb-4 md:mb-6">
        <div className="flex items-baseline gap-2">
          <span className="text-4xl md:text-5xl font-bold text-white leading-none">
            ${isAnnual ? monthlyEquivalent.toFixed(2) : monthlyPrice.toFixed(2)}
          </span>
          <span className="text-base md:text-lg text-white/70">/month</span>
        </div>
        {isAnnual && (
          <p className="text-xs md:text-sm text-white/70 mt-1">
            Billed annually (${annualPrice.toFixed(2)}/year)
          </p>
        )}
      </div>

      {/* CTA Button - Touch-friendly on mobile (min 44x44px) */}
      <Button
        className={cn(
          'w-full py-4 md:py-6 text-base font-semibold rounded-full transition-all min-h-[44px]',
          isRecommended
            ? 'bg-[#5B6FED] hover:bg-[#4C5FD8] text-white'
            : 'bg-gray-100 hover:bg-gray-200 text-gray-900',
          isCurrentPlan && 'opacity-50 cursor-not-allowed'
        )}
        disabled={isCurrentPlan || loading || !priceId}
        onClick={() => {
          // Track pricing card click
          trackPricingCardInteraction(plan, isAnnual, 'click');
          
          console.log('[PricingCard] Button clicked:', {
            plan,
            isAnnual,
            isCurrentPlan,
            loading,
            priceId,
          });

          if (priceId) {
            onSubscribe(priceId);
          }
        }}
      >
        {loading ? 'Processing...' : isCurrentPlan ? 'Current Plan' : isRecommended ? 'Start Saving Now' : 'Get Started'}
      </Button>

      {/* Features List */}
      <div className="mt-6 md:mt-8 space-y-3 md:space-y-4 pt-6 md:pt-8 border-t border-gray-200">
        {benefits.map((feature, idx) => (
          <div key={idx} className="flex items-start gap-2 md:gap-3">
            <Check className="w-4 h-4 md:w-5 md:h-5 text-[#5B6FED] flex-shrink-0 mt-0.5" />
            <span className="text-white/90 text-sm md:text-base leading-relaxed">{feature}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

